import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import providerService from '../services/providerService';
import categoryService from '../services/categoryService';
import fileUpload from '../utils/fileUpload';
import fileDelete from '../utils/fileDelete';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ProvidersPage.css';

const ProvidersPage = () => {
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [name, setName] = useState('');
  const [image, setImage] = useState(null);
  const [parentCategory, setParentCategory] = useState('');
  const [editingProvider, setEditingProvider] = useState(null);
  const [loading, setLoading] = useState(false);
  // নতুন স্টেট ফিল্টারিংয়ের জন্য
  const [filterName, setFilterName] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  useEffect(() => {
    const apiKey = localStorage.getItem('apiKey');
    const token = localStorage.getItem('token');
    if (!token || !apiKey) {
      toast.error('দয়া করে প্রথমে লগইন করুন এবং API কী সেট করুন', { position: 'top-right' });
      navigate('/login');
      return;
    }
    loadCategories();
    loadProviders();
  }, [navigate]);

  const loadCategories = async () => {
    try {
      const response = await categoryService.getCategories();
      setCategories(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (error) {
      toast.error('ক্যাটাগরি লোড করতে সমস্যা: ' + (error.response?.data?.error || error.message), {
        position: 'top-right',
      });
    }
  };

  const loadProviders = async () => {
    setLoading(true);
    try {
      const response = await providerService.getProviders();
      setProviders(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (error) {
      toast.error('প্রোভাইডার লোড করতে সমস্যা: ' + (error.response?.data?.error || error.message), {
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let imageUrl = editingProvider ? editingProvider.image : '';
      if (image) {
        imageUrl = await fileUpload(image);
      }
      const providerData = { name, image: imageUrl, parentcategory: parentCategory };
      
      if (editingProvider) {
        const response = await providerService.updateProvider(editingProvider._id, providerData);
        toast.success('প্রোভাইডার সফলভাবে আপডেট হয়েছে!', { position: 'top-right' });
      } else {
        const response = await providerService.createProvider(providerData);
        toast.success('প্রোভাইডার সফলভাবে তৈরি হয়েছে!', { position: 'top-right' });
      }
      resetForm();
      loadProviders();
    } catch (error) {
      toast.error(
        `${editingProvider ? 'আপডেট' : 'তৈরি'} ত্রুটি: ` + (error.response?.data?.error || error.message),
        { position: 'top-right' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (provider) => {
    setName(provider.name);
    setImage(null);
    setParentCategory(provider.parentcategory?._id || '');
    setEditingProvider(provider);
  };

  const handleDelete = async (id) => {
    if (window.confirm('আপনি কি এই প্রোভাইডারটি মুছে ফেলতে চান?')) {
      setLoading(true);
      try {
        const provider = providers.find((prov) => prov._id === id);
        if (!provider) {
          throw new Error('প্রোভাইডার পাওয়া যায়নি।');
        }
        if (provider.image) {
          await fileDelete(provider.image);
        }
        await providerService.deleteProvider(id);
        toast.success('প্রোভাইডার সফলভাবে মুছে ফেলা হয়েছে!', { position: 'top-right' });
        loadProviders();
      } catch (error) {
        toast.error('মুছে ফেলার ত্রুটি: ' + (error.response?.data?.error || error.message), {
          position: 'top-right',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = () => {
    setName('');
    setImage(null);
    setParentCategory('');
    setEditingProvider(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ফিল্টার করা প্রোভাইডার তালিকা তৈরি
  const filteredProviders = providers.filter((provider) => {
    const matchesName = provider.name.toLowerCase().includes(filterName.toLowerCase());
    const matchesCategory = filterCategory ? provider.parentcategory?._id === filterCategory : true;
    return matchesName && matchesCategory;
  });

  // ফিল্টার রিসেট করার ফাংশন
  const resetFilters = () => {
    setFilterName('');
    setFilterCategory('');
  };

  return (
    <div className="provider-dashboard">
      <Header />
      <ToastContainer />
      <div className="container py-5">
        <div className="row g-4">
          <div className="col-lg-12">
            <div className="card shadow-lg p-4 gradient-bg">
              <h2 className="text-center mb-4 text-white">
                {editingProvider ? 'প্রোভাইডার আপডেট' : 'নতুন প্রোভাইডার তৈরি'}
              </h2>
              <form onSubmit={handleCreate}>
                <div className="mb-3">
                  <label htmlFor="name" className="form-label text-white">প্রোভাইডারের নাম</label>
                  <input
                    type="text"
                    className="form-control rounded-pill"
                    id="name"
                    placeholder="প্রোভাইডারের নাম লিখুন"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="parentcategory" className="form-label text-white">প্যারেন্ট ক্যাটাগরি</label>
                  <select
                    id="parentcategory"
                    className="form-control rounded-pill"
                    value={parentCategory}
                    onChange={(e) => setParentCategory(e.target.value)}
                    required
                    disabled={loading}
                  >
                    <option value="" disabled>ক্যাটাগরি নির্বাচন করুন</option>
                    {categories.map((category) => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label htmlFor="image" className="form-label text-white">ইমেজ নির্বাচন করুন</label>
                  <input
                    type="file"
                    className="form-control"
                    id="image"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={(e) => setImage(e.target.files[0])}
                    disabled={loading}
                  />
                  {editingProvider && editingProvider.image && (
                    <div className="mt-2">
                      <p className="text-white">বর্তমান ইমেজ:</p>
                      <img
                        src={`${process.env.REACT_APP_API_URL}/${editingProvider.image}`}
                        alt="Current provider"
                        style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                      />
                    </div>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="submit"
                    className="btn btn-register rounded-pill px-4"
                    disabled={loading}
                  >
                    {editingProvider ? <FaEdit /> : <FaPlus />}
                    {editingProvider ? ' আপডেট' : ' তৈরি'}
                  </button>
                  {editingProvider && (
                    <button
                      type="button"
                      className="btn btn-cancel rounded-pill px-4"
                      onClick={resetForm}
                      disabled={loading}
                    >
                      বাতিল
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
          <div className="col-lg-12 mt-4">
            <div className="card shadow-lg p-4">
              <h2 className="text-center mb-4 gradient-text">প্রোভাইডার তালিকা</h2>
              {/* ফিল্টার ফর্ম */}
              <div className="mb-4">
                <h4 className="mb-3">ফিল্টার প্রোভাইডার</h4>
                <div className="row g-3">
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control rounded-pill"
                      placeholder="নাম দিয়ে ফিল্টার করুন"
                      value={filterName}
                      onChange={(e) => setFilterName(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                  <div className="col-md-4">
                    <select
                      className="form-control rounded-pill"
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      disabled={loading}
                    >
                      <option value="">সব ক্যাটাগরি</option>
                      {categories.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <button
                      className="btn btn-cancel rounded-pill px-4"
                      onClick={resetFilters}
                      disabled={loading}
                    >
                      ফিল্টার রিসেট
                    </button>
                  </div>
                </div>
              </div>
              {loading && <div className="alert alert-info">লোড হচ্ছে...</div>}
              {filteredProviders.length === 0 && !loading && (
                <div className="alert alert-warning">কোনো প্রোভাইডার পাওয়া যায়নি।</div>
              )}
              {filteredProviders.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-hover table-bordered">
                    <thead className="gradient-bg text-white">
                      <tr>
                        <th>ইমেজ</th>
                        <th>নাম</th>
                        <th>প্যারেন্ট ক্যাটাগরি</th>
                        <th>অ্যাকশন</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProviders.map((provider) => (
                        <tr key={provider._id} className="table-row-hover">
                          <td>
                            {provider.image ? (
                              <img
                                src={`${process.env.REACT_APP_API_URL}/${provider.image}`}
                                alt={provider.name}
                                style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                              />
                            ) : (
                              'কোনো ইমেজ নেই'
                            )}
                          </td>
                          <td>{provider.name}</td>
                          <td>{provider.parentcategory?.name || 'N/A'}</td>
                          <td>
                            <button
                              className="btn btn-edit me-2"
                              onClick={() => handleEdit(provider)}
                              disabled={loading}
                            >
                              <FaEdit /> এডিট
                            </button>
                            <button
                              className="btn btn-delete"
                              onClick={() => handleDelete(provider._id)}
                              disabled={loading}
                            >
                              <FaTrash /> মুছুন
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProvidersPage;